import { motion } from 'framer-motion';
import { Instagram, ExternalLink, Star, MapPin, ArrowRight } from 'lucide-react';

const projects = [
  {
    id: 1,
    name: "Pete's Hot Chicken",
    type: "Restaurant",
    location: "San Antonio, TX",
    url: "https://peteshotchickentx.com/",
    screenshot: "https://customer-assets.emergentagent.com/job_startup-sites-5/artifacts/4zrruxsc_Screenshot%202026-03-24%20164108.png",
    rating: "4.7",
  },
];

export const GallerySection = () => {
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="gallery" className="py-28 md:py-36 bg-[#0a0a0a] relative overflow-hidden" data-testid="gallery-section">
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-[0.03]" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <p className="text-[#e62020] text-xs tracking-[0.2em] uppercase mb-5">
            Our work
          </p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            data-testid="gallery-headline"
          >
            Real sites.
            <br />
            <span className="text-[#888888]">Real businesses.</span>
          </h2>
        </motion.div>

        {/* Featured Project - larger, prominent */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Main project */}
          {projects.map((project) => (
            <motion.a
              key={project.id}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -5 }}
              className="group lg:col-span-8 relative bg-[#111111] border border-[#1a1a1a] hover:border-[#e62020]/40 overflow-hidden transition-colors"
              data-testid={`gallery-project-${project.id}`}
            >
              {/* Browser mockup */}
              <div className="relative">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0d0d0d] border-b border-[#1a1a1a]">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-[#e62020]/50" />
                    <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
                    <div className="w-2 h-2 rounded-full bg-green-500/50" />
                  </div>
                  <div className="flex-1 mx-2">
                    <div className="bg-[#0a0a0a] border border-[#1a1a1a] px-3 py-1 text-[10px] font-mono text-[#888888] truncate max-w-[200px]">
                      {project.url.replace('https://', '')}
                    </div>
                  </div>
                </div>
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img 
                    src={project.screenshot} 
                    alt={project.name}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
              </div>

              {/* Badges */}
              <div className="absolute top-12 left-3 flex items-center gap-1.5 bg-[#0a0a0a]/90 border border-[#1a1a1a] px-2 py-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
                <span className="text-[9px] font-mono text-green-400 uppercase">Live</span>
              </div>
              <div className="absolute top-12 right-3 flex items-center gap-1 bg-[#0a0a0a]/90 border border-[#1a1a1a] px-2 py-1">
                <Star size={10} className="text-yellow-500 fill-yellow-500" />
                <span className="text-[10px] font-mono text-white">{project.rating}</span>
              </div>

              {/* Info */}
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#e62020] text-[10px] uppercase tracking-wider mb-1">{project.type}</p>
                    <h3 
                      className="text-xl font-bold uppercase tracking-tight text-white"
                      style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                    >
                      {project.name}
                    </h3>
                    <div className="flex items-center gap-1 text-[#888888] text-xs mt-1">
                      <MapPin size={10} />
                      <span>{project.location}</span>
                    </div>
                  </div>
                  <div className="text-[#e62020] group-hover:translate-x-1 transition-transform">
                    <ExternalLink size={16} />
                  </div>
                </div>
              </div>
            </motion.a>
          ))}

          {/* CTA Card - single, honest */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.55, delay: 0.1 }}
            onClick={scrollToContact}
            className="lg:col-span-4 bg-[#111111] border border-[#1a1a1a] hover:border-[#e62020]/30 p-8 flex flex-col justify-center cursor-pointer transition-colors group"
          >
            <p className="text-[#888888] text-sm mb-4">More projects in progress.</p>
            <h3 
              className="text-2xl md:text-3xl font-bold uppercase leading-[0.95] text-white mb-2"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              Yours could be
              <span className="text-[#e62020]"> next.</span>
            </h3>
            <motion.button
              className="inline-flex items-center gap-2 text-[#e62020] text-sm font-medium mt-4 group-hover:translate-x-1 transition-transform"
              whileHover={{ x: 3 }}
            >
              Get started <ArrowRight size={14} />
            </motion.button>
          </motion.div>
        </div>

        {/* Instagram link */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-4"
        >
          <a
            href="https://www.instagram.com/vortexwebco/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#888888] hover:text-white transition-colors text-sm"
            data-testid="instagram-link"
          >
            <Instagram size={16} />
            <span>Follow along @vortexwebco</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
